import { Routes, Route, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/App";
import {
  LayoutDashboard,
  FileText,
  MessageSquare,
  Mail,
  Users,
  FolderKanban,
  Quote,
  Newspaper,
  UserPlus,
} from "lucide-react";

import DashboardHome from "@/components/admin/DashboardHome";
import ArticlesManagement from "@/components/admin/ArticlesManagement";
import CommentsManagement from "@/components/admin/CommentsManagement";
import ContactsManagement from "@/components/admin/ContactsManagement";
import UsersManagement from "@/components/admin/UsersManagement";
import ProjectsManagement from "@/components/admin/ProjectsManagement";
import TestimonialsManagement from "@/components/admin/TestimonialsManagement";
import NewsletterManagement from "@/components/admin/NewsletterManagement";
import ApplicationsManagement from "@/components/admin/ApplicationsManagement";

// Access Denied Component
const AccessDenied = () => (
  <div className="text-center py-12">
    <p className="text-muted-foreground">Accès réservé à l'administrateur</p>
  </div>
);

const AdminDashboard = () => {
  const location = useLocation();
  const { user } = useAuth();

  const isAdmin = user?.role === "admin";

  // Navigation items - some are admin-only
  const navItems = [
    { path: "/admin", icon: LayoutDashboard, label: "Tableau de bord", roles: ["admin", "editor"] },
    { path: "/admin/articles", icon: FileText, label: "Articles", roles: ["admin", "editor"] },
    { path: "/admin/projects", icon: FolderKanban, label: "Projets", roles: ["admin"] },
    { path: "/admin/testimonials", icon: Quote, label: "Témoignages", roles: ["admin"] },
    { path: "/admin/applications", icon: UserPlus, label: "Candidatures", roles: ["admin"] },
    { path: "/admin/newsletter", icon: Newspaper, label: "Newsletter", roles: ["admin"] },
    { path: "/admin/comments", icon: MessageSquare, label: "Commentaires", roles: ["admin", "editor"] },
    { path: "/admin/contacts", icon: Mail, label: "Messages", roles: ["admin", "editor"] },
    { path: "/admin/users", icon: Users, label: "Utilisateurs", roles: ["admin"] },
  ].filter(item => item.roles.includes(user?.role));

  const isActive = (path) => {
    if (path === "/admin") return location.pathname === "/admin";
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen pt-20 md:pt-24 pb-12 md:pb-16" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="mb-4 sm:mb-6 md:mb-8">
            <h1 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-1">
              Administration
            </h1>
            <p className="text-xs sm:text-sm md:text-base text-muted-foreground">
              Bienvenue, {user?.full_name}
            </p>
          </div>

          {/* Mobile Navigation - Horizontal scroll */}
          <div className="lg:hidden mb-4 sm:mb-6 -mx-3 sm:-mx-6 px-3 sm:px-6">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`admin-nav-mobile-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-full transition-colors whitespace-nowrap text-xs sm:text-sm flex-shrink-0 ${
                    isActive(item.path)
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <item.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="flex flex-col lg:flex-row lg:gap-8">
            {/* Desktop Sidebar */}
            <nav className="hidden lg:block w-[220px] xl:w-[250px] flex-shrink-0 space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                    isActive(item.path)
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Content */}
            <div className="flex-1 min-h-[400px] lg:min-h-[500px]">
              <Routes>
                <Route index element={<DashboardHome />} />
                <Route path="articles" element={<ArticlesManagement />} />
                <Route path="projects" element={isAdmin ? <ProjectsManagement /> : <AccessDenied />} />
                <Route path="testimonials" element={isAdmin ? <TestimonialsManagement /> : <AccessDenied />} />
                <Route path="applications" element={isAdmin ? <ApplicationsManagement /> : <AccessDenied />} />
                <Route path="newsletter" element={isAdmin ? <NewsletterManagement /> : <AccessDenied />} />
                <Route path="comments" element={<CommentsManagement />} />
                <Route path="contacts" element={<ContactsManagement />} />
                <Route path="users" element={isAdmin ? <UsersManagement /> : <AccessDenied />} />
              </Routes>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminDashboard;
